import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import API from '../../config/API';
import { newServicesAction } from '../../_actions';
import { productAction } from '../../_actions/product.action';
import { stocktakeService } from '../../_services';
import { formService } from '../../_services/form.service';
import { giftCardService } from '../../_services/giftCard.service';
import { sitesService } from '../../_services/sites.service';
import { TAXES_VISIBLE } from '../../config/featureFlags';
import { productService } from '../../_services/product.service';
import { ImportHero } from '../settings/ImportData';
import { getUserInfo } from '../stocktake/StocktakeUtils';
import useSites from '../multiLocation/useSites';
import CatalogueTopNav, { tabIcons } from '../new-service/CatalogueTopNav';
import '../settings/SettingsHub.css';
import './CatalogueOverview.css';

/**
 * Catalogue landing (/catalogue): most popular and most profitable service
 * over the last 30 days, the import panel, and section cards
 * with a real count and state on every row.
 */

const authHeaders = () => ({ 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') });
const daysAgo = (iso) => { const t = new Date(iso).getTime(); return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86400000) : null; };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch (e) { return ''; } };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function CatalogueOverview({ history, user }) {
  const dispatch = useDispatch();
  const { multi } = useSites();
  const [d, setD] = useState({ services: null, products: null, vouchers: null, forms: null, stocktake: null, resources: null, transfers: null, report: null });
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    let alive = true;
    const keep = (k) => (v) => { if (alive) set(k, v); };
    dispatch(newServicesAction.listServicesData({ hairdresserId: '', onlineBooking: '', status: '', search: '' })).then((r) => keep('services')(r?.listServices || [])).catch(() => keep('services')([]));
    dispatch(productAction.listProduct({ page: 0, limit: 200, search: '', categoryId: '', brandId: '', stockStatus: '' })).then((r) => keep('products')(r?.data?.responseData?.result || [])).catch(() => keep('products')([]));
    giftCardService.listVouchers().then(keep('vouchers')).catch(() => keep('vouchers')([]));
    formService.listForms({}).then((r) => keep('forms')(r?.data?.responseData?.result || [])).catch(() => keep('forms')([]));
    try {
      const { salonId } = getUserInfo();
      stocktakeService.getStocktakeHistory({ page: 1, limit: 1, salonId }).then((r) => {
        const rd = r?.data?.responseData || r?.data || {};
        const list = rd.data?.stocktakes || rd.result?.stocktakes || rd.stocktakes || rd.result || [];
        keep('stocktake')(Array.isArray(list) ? list[0] || null : null);
      }).catch(() => keep('stocktake')(null));
    } catch (e) { keep('stocktake')(null); }
    // Last 30 days of the real service report: most booked and best £/hour.
    const end = new Date(); const start = new Date(Date.now() - 30 * 86400000);
    productService.getServiceReport({ startDate: start.toISOString(), endDate: end.toISOString() })
      .then((r) => { const data = r && r.data ? (r.data.responseData !== undefined ? r.data.responseData : r.data) : null; keep('report')(data || {}); })
      .catch(() => keep('report')({}));
    axios.get(API.RESOURCE_LIST, { headers: authHeaders() }).then((r) => keep('resources')(r?.data?.responseData || { groups: [], resources: [] })).catch(() => keep('resources')({ groups: [], resources: [] }));
    return () => { alive = false; };
  }, [dispatch]);
  useEffect(() => {
    if (!multi) return undefined;
    let alive = true;
    sitesService.stockTransfers({}).then((r) => { if (alive) set('transfers', (r && r.transfers) || []); }).catch(() => { if (alive) set('transfers', []); });
    return () => { alive = false; };
  }, [multi]);

  // ---- derive ----
  const services = useMemo(() => {
    // Featured categories repeat services that already live in a real
    // category, and a service can appear once per stylist: count each once.
    const seen = new Set();
    const out = [];
    (d.services || []).filter((cat) => cat && !cat.isFeaturedCategory).forEach((cat) => (cat?.serviceList || []).forEach((s) => {
      const id = String(s.commonServiceId || s._id || s.serviceName);
      if (seen.has(id)) return;
      seen.add(id);
      out.push({
        id, name: s.serviceName, price: Number(s.servicePrice) || 0, fromPrice: Number(s.fromPrice) || 0,
        processing: Number(s.processingTime) || 0, staffCount: Number(s.staffCount) || 0, notAssigned: !!s.notAssigned, category: cat.categoryName,
      });
    }));
    return out;
  }, [d.services]);
  const categories = (d.services || []).filter((c) => c && !c.isFeaturedCategory).length;
  const noStaff = services.filter((s) => s.notAssigned || s.staffCount === 0);
  const noPrice = services.filter((s) => !s.price && !s.fromPrice);
  const products = d.products || [];
  const tracked = products.filter((p) => Number(p.trackStock) === 1);
  const outOfStock = tracked.filter((p) => Number(p.quantity) <= 0);
  const lowStock = tracked.filter((p) => Number(p.quantity) > 0 && Number(p.reorderLevel || p.lowStockThreshold || 0) > 0 && Number(p.quantity) <= Number(p.reorderLevel || p.lowStockThreshold));
  const mySalon = String(localStorage.getItem('salonId') || '');
  const toReceive = (d.transfers || []).filter((t) => t.status === 'sent' && String(t.toSalonId) === mySalon);
  const stockDate = d.stocktake ? (d.stocktake.completedAt || d.stocktake.lastEditTime || d.stocktake.date || d.stocktake.createdAt || d.stocktake.created) : null;
  const stockAge = stockDate ? daysAgo(stockDate) : null;
  const groups = d.resources?.groups || [];
  const units = d.resources?.resources || [];
  const vouchers = d.vouchers || [];
  const forms = d.forms || [];

  const isOwner = user?.userInfo?.hairdresser?.isSalonOwner === 1;
  // A self-employed stylist is the seller on their own sales, so they set
  // their own tax rates (docs/tax-money-path.md) — the Taxes card is theirs too.
  const isSelfEmployed = !isOwner && Number(user?.userInfo?.hairdresser?.employmentTypeId) === 2;
  const currency = user?.userInfo?.hairdresser?.currencySymbol || '£';
  const money = (n) => `${currency}${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const serviceNames = useMemo(() => {
    const m = {};
    (d.services || []).forEach((cat) => (cat?.serviceList || []).forEach((s) => {
      if (s._id) m[String(s._id)] = s.serviceName;
      if (s.commonServiceId) m[String(s.commonServiceId)] = s.serviceName;
    }));
    return m;
  }, [d.services]);
  const named = (list) => list.map((x) => ({ ...x, name: x.name || x.serviceName || serviceNames[String(x.serviceId)] || '' })).filter((x) => x.name);
  const topList = named((d.report && d.report.topServices) || []);
  const popular = topList.length ? topList.reduce((a, b) => ((b.bookings || 0) > (a.bookings || 0) ? b : a)) : null;
  const profitable = named((d.report && d.report.mostProfitable) || [])[0] || null;
  const rows1 = [
    { id: 'services', label: 'Services', path: '/new-services', desc: d.services === null ? 'Loading…' : services.length ? `${plural(services.length, 'service', 'services')} across ${plural(categories, 'category', 'categories')}` : 'Nothing bookable yet', pills: [noStaff.length && { tone: 'warn', t: `${noStaff.length} unassigned` }, noPrice.length && { tone: 'warn', t: `${noPrice.length} no price` }] },
    ...(isOwner ? [{ id: 'resources', label: 'Resources', path: '/resources', desc: groups.length ? groups.map((g) => `${plural(units.filter((u) => String(u.groupId) === String(g._id)).length, g.name.replace(/s$/, '').toLowerCase(), g.name.toLowerCase())}`).join(', ') : 'Rooms, basins, chairs', pills: [] }] : []),
    ...(isOwner ? [{ id: 'forms', label: 'Forms', path: '/forms', desc: forms.length ? plural(forms.length, 'form', 'forms') : 'Consultation and consent forms', pills: [] }] : []),
  ];
  const rows2 = isOwner ? [
    { id: 'products', label: 'Products', path: '/product', desc: products.length ? plural(products.length, 'product', 'products') : 'No products yet', pills: [outOfStock.length && { tone: 'bad', t: `${outOfStock.length} out of stock` }, lowStock.length && { tone: 'warn', t: `${lowStock.length} low` }] },
    { id: 'stocktake', label: 'Stock take', path: '/stocktake', desc: stockDate ? `Last counted ${fmtDay(stockDate)}` : 'Never counted', pills: [stockAge !== null && stockAge > 30 && { tone: 'warn', t: `${stockAge} days ago` }] },
    ...(multi ? [{ id: 'stock-transfers', label: 'Stock transfers', path: '/stock-transfers', desc: 'Between your locations', pills: [toReceive.length && { tone: 'warn', t: `${toReceive.length} to receive` }] }] : []),
    { id: 'vouchers-v2', label: 'Vouchers', path: '/voucher-v2', desc: vouchers.length ? `${plural(vouchers.length, 'voucher', 'vouchers')} on sale` : 'Gift cards and vouchers', pills: [] },
    { id: 'taxes', label: 'Taxes', path: '/taxes', desc: 'Tax rates on services and products', pills: [] },
  ] : isSelfEmployed && TAXES_VISIBLE ? [
    { id: 'taxes', label: 'Taxes', path: '/taxes', desc: 'Your own tax rates, on your own sales', pills: [] },
  ] : [];

  const Row = ({ r }) => (
    <button type="button" className="settings-hub-row" onClick={() => history.push(r.path)}>
      <span className="settings-hub-row-icon">{tabIcons[r.id] || null}</span>
      <span className="settings-hub-row-text">
        <b>{r.label}</b>
        <span>{r.desc}</span>
        {r.pills.filter(Boolean).length > 0 && <span className="co-pills">{r.pills.filter(Boolean).map((p) => <span key={p.t} className={`co-pill is-${p.tone}`}>{p.t}</span>)}</span>}
      </span>
      <span className="settings-hub-row-chevron"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg></span>
    </button>
  );

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          <CatalogueTopNav />
          <div className="catalogue-content-container">
            <div className="settings-hub co-page">
              <div className="co-head">
                <h1 className="settings-hub-title">Catalogue</h1>
              </div>

              <div className="co-tiles">
                {[
                  { key: 'popular', label: 'Most popular', item: popular, icon: <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z" />,
                    stats: popular ? [[String(popular.bookings || 0), plural(popular.bookings || 0, 'booking', 'bookings').replace(/^\d+ /, '')], [money(popular.revenue), 'revenue']] : [] },
                  { key: 'profitable', label: 'Most profitable', item: profitable, icon: <path d="M3 17l6-6 4 4 8-8M14 7h7v7" />,
                    stats: profitable ? [[money(profitable.revenuePerHour), 'an hour'], [String(profitable.bookings || 0), plural(profitable.bookings || 0, 'booking', 'bookings').replace(/^\d+ /, '')]] : [] },
                ].map((t) => (
                  <button type="button" className="co-tile" key={t.key} onClick={() => history.push('/new-services')}>
                    <span className="co-tile-label">
                      <svg className="co-tile-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{t.icon}</svg>
                      <span>{t.label}</span>
                      <span className="co-tile-label-dim">30 days</span>
                    </span>
                    {d.report === null ? (
                      <>
                        <span className="co-tile-skel" />
                        <span className="co-tile-skel co-tile-skel--short" />
                      </>
                    ) : t.item ? (
                      <>
                        <span className="co-tile-value">{t.item.name}</span>
                        <span className="co-tile-stats">
                          {t.stats.map(([v, l]) => <span className="co-tile-stat" key={l}><b>{v}</b><small>{l}</small></span>)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="co-tile-value co-tile-value--empty">Nothing yet</span>
                        <span className="co-tile-empty">Shows once you have completed bookings.</span>
                      </>
                    )}
                  </button>
                ))}
                <div className="co-tile co-tile--import"><ImportHero compact /></div>
              </div>

              <div className="settings-hub-groups">
                <section className="settings-hub-group">
                  <div className="settings-hub-group-head"><h2>Services and bookings</h2><small>What clients can book</small></div>
                  {rows1.map((r) => <Row key={r.id} r={r} />)}
                </section>
                {rows2.length > 0 && (
                  <section className="settings-hub-group">
                    <div className="settings-hub-group-head"><h2>Retail and stock</h2><small>What you sell and hold</small></div>
                    {rows2.map((r) => <Row key={r.id} r={r} />)}
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRouter(connect((state) => ({ user: state.user }))(CatalogueOverview));
